import {
  Backdrop,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControl,
  FormControlLabel,
  IconButton,
  Input,
  InputAdornment,
  InputLabel,
  TextField,
  Typography,
} from '@mui/material';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import VisibilityIcon from '@mui/icons-material/Visibility';
import React, { useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import CheckIcon from '@mui/icons-material/Check';
import AuthLayout from '../authenticate/Layout';
import { api } from 'src/api/endpoints';

const imNotArobotButtonStyles = {
  minWidth: '250px',
  height: '50px',
  padding: '10px',
  backgroundColor: '#F9F9F9',
  border: '1px solid #C1C1C1',
  borderRadius: '5px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  color: '#000',
  fontSize: '15px',
  cursor: 'pointer',
};

function PromoLanding({ setStep, setUserId }) {
  const [isLoading, setIsLoading] = useState(false);
  const { t } = useTranslation();
  const [type, setType] = useState(true);
  const [confirmPasswordType, setConfirmPasswordType] = useState(true);
  const {
    register,
    setValue,
    handleSubmit,
    getValues,
    reset,
    watch,
    control,
    formState: { errors },
  } = useForm({
    mode: 'onTouched',
  });

  const onSubmit = async (data) => {
    try {
      const resp = await api.post('/user/register', {
        fullname: data.fullname,
        email: data.email,
        password: data.password,
        confirmPassword: data.confirmPassword,
        role: 'user',
        captcha: data.captcha,
      });
      await api.post('/user/login', {
        email: data.email,
        password: data.password,
      });
      window.location.replace(`/user-details/${resp.data.user._id}#mysubscriptionsfl`)
      // setStep(1)
      reset({
        fullname: '',
        email: '',
        password: '',
        confirmPassword: '',
        captcha: '',
      });
    } catch (error) {
      if (error?.response?.data?.message) {
        if (error.response.data.message == 'reCaptchaErr') {
          toast.error(t('views.signup.recaptchaFailed'));
        } else if (error.response.data.message == 'noReCaptcha') {
          toast.error(t('views.signup.recaptchaErr'));
        } else {
          toast.error(error.response.data.message);
        }
      }
    }
  };

  const ReCaptchaComponent = () => {
    const { executeRecaptcha } = useGoogleReCaptcha();
    const handleReCaptchaVerify = useCallback(async () => {
      if (!executeRecaptcha) {
        console.log('Execute recaptcha not yet available');
        return;
      }

      const token = await executeRecaptcha('yourAction');
      setValue('captcha', token);
    }, [executeRecaptcha]);
    return (
      <button
        style={imNotArobotButtonStyles}
        type='button'
        onClick={handleReCaptchaVerify}
      >
        <div
          style={{
            width: '30px',
            height: '30px',
            border: '2px solid #C1C1C1',
            borderRadius: '3px',
          }}
        >
          {watch('captcha') ? <CheckIcon color='success' /> : null}
        </div>
        I'm not a robot
        <img
          src='/images/recaptcha-icon.png'
          alt=''
          style={{ width: '50px' }}
        />
      </button>
      // <Button variant="outlined" fullWidth endIcon={watch("captcha") ? <CheckIcon/> : null} onClick={handleReCaptchaVerify}>{t("views.signup.notRobot")}</Button>
    );
  };

  return (
    <>
      <Backdrop
        sx={(theme) => ({ color: '#fff', zIndex: theme.zIndex.drawer + 1 })}
        open={isLoading}
      >
        <CircularProgress color='inherit' />
      </Backdrop>
      <AuthLayout>
        <Typography
          textAlign='center'
          maxWidth='460px'
          variant='h3'
          sx={{
            color: '#016AFE',
          }}
        >
          VCPlayer
        </Typography>
        <Typography
          textAlign='center'
          maxWidth='460px'
          variant='h4'
          sx={{
            color: '#313031',
          }}
        >
          SIGNAGE
        </Typography>
        <form
          onSubmit={handleSubmit(onSubmit)}
          noValidate
          style={{
            width: '100%',
            maxWidth: '460px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexDirection: 'column',
            gap: '20px',
          }}
        >
          <TextField
            {...register('fullname', {
              required: true,
            })}
            variant={'standard'}
            label={t('views.signup.fullName')}
            type={'text'}
            fullWidth
          />
          {errors.fullname && (
            <Typography sx={{ color: 'warning.main' }}>
              {errors.fullname?.type == 'required'
                ? t('views.signup.nameRequired')
                : errors.fullname.message}
            </Typography>
          )}

          <TextField
            {...register('email', {
              required: true,
              pattern: {
                value:
                  /^[a-zA-Z0-9.!#$%&’*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)*$/,
                message: t('views.signup.invalidEmail'),
              },
            })}
            variant={'standard'}
            label={t('views.login.email')}
            type={'email'}
            fullWidth
          />
          {errors.email && (
            <Typography sx={{ color: 'warning.main' }}>
              {errors.email?.type == 'required'
                ? t('views.login.emailRequired')
                : errors.email.message}
            </Typography>
          )}

          <FormControl variant={'standard'} fullWidth>
            <InputLabel htmlFor='password'>
              {t('views.login.password')}
            </InputLabel>
            <Input
              id='password'
              type={type ? 'password' : 'text'}
              sx={{ background: 'transparent', color: '#313031' }}
              {...register('password', {
                required: true,
                minLength: 6,
              })}
              endAdornment={
                <InputAdornment
                  position={'end'}
                  style={{ marginTop: '-10px', color: '#551fff' }}
                >
                  <IconButton
                    aria-label='toggle password visibility'
                    onClick={() => setType(!type)}
                  >
                    {type ? (
                      <VisibilityOffIcon style={{ color: '#551fff' }} />
                    ) : (
                      <VisibilityIcon style={{ color: '#551fff' }} />
                    )}
                  </IconButton>
                </InputAdornment>
              }
            />
          </FormControl>
          {errors.password && (
            <Typography sx={{ color: 'warning.main' }}>
              {errors.password?.type == 'required'
                ? t('views.login.passwordRequired')
                : errors.password?.type == 'minLength'
                  ? t('views.login.passToShort')
                  : errors.password.message}
            </Typography>
          )}

          <FormControl variant={'standard'} fullWidth>
            <InputLabel htmlFor='confirmPassword'>
              {t('views.signup.confirmPassword')}
            </InputLabel>
            <Input
              id='confirmPassword'
              type={confirmPasswordType ? 'password' : 'text'}
              sx={{ color: '#313031' }}
              {...register('confirmPassword', {
                required: true,
                validate: (value) =>
                  value === getValues('password') ||
                  t('views.signup.passwordMismatch'),
              })}
              endAdornment={
                <InputAdornment
                  position={'end'}
                  style={{ marginTop: '-10px', color: '#551fff' }}
                >
                  <IconButton
                    aria-label='toggle password visibility'
                    onClick={() => setConfirmPasswordType(!confirmPasswordType)}
                  >
                    {confirmPasswordType ? (
                      <VisibilityOffIcon style={{ color: '#551fff' }} />
                    ) : (
                      <VisibilityIcon style={{ color: '#551fff' }} />
                    )}
                  </IconButton>
                </InputAdornment>
              }
            />
          </FormControl>
          {errors.confirmPassword && (
            <Typography color='warning.main'>
              {errors.confirmPassword?.type == 'required'
                ? t('views.login.passwordRequired')
                : errors.confirmPassword?.message}
            </Typography>
          )}

          <Box
            sx={{
              height: '50px',
              width: '100%',
              margin: '5px auto',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <Box
              sx={{
                alignItems: 'center',
                display: 'flex',
                ml: '-9px',
                width: '100%',
              }}
            >
              <Controller
                control={control}
                name={'policy'}
                defaultValue={false}
                rules={{ required: true }}
                render={({ field }) => (
                  <>
                    <Checkbox {...field} />
                    <Typography color='text.secondary' variant='body1'>
                      I have read the{' '}
                      <a href='https://vcplayer.com/terms-and-conditions'>
                        Terms and Conditions
                      </a>
                    </Typography>
                  </>
                )}
              />
            </Box>
            <Typography sx={{ color: 'error.main' }}>
              {errors?.policy && 'You need to accept Terms and Conditions'}
            </Typography>
          </Box>

          <Box
            sx={{
              height: '50px',
            }}
          >
            <ReCaptchaComponent />
          </Box>

          <Box>
            <Button
              variant='contained'
              type='submit'
              fullWidth
              sx={{
                '&.Mui-disabled': {
                  background: '#d3d3d3ff',
                  color: '#919191ff',
                },
              }}
            >
              {t('views.signup.signUp')}
            </Button>
          </Box>

          <Box sx={{
                  display: "flex",
                  justifyContent: "center",
                  gap: "10px"
              }}>
                  <Typography sx={{
                    color: "#313031"
                  }}>Do you already have an account?</Typography>
                  <Link to={'/login'} style={{ textDecoration: "none", color: "#016AFE" }}>Login</Link>
              </Box>
        </form>
      </AuthLayout>
    </>
  );
}

export default PromoLanding;
